import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Divider,
  Drawer,
  Hidden,
  makeStyles,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  Grid,
  CardMedia,
  Typography,
  CircularProgress,
  Box,
  useScrollTrigger,
  Slide,
} from '@material-ui/core';
import { Menu } from '@material-ui/icons';
import { Link } from 'gatsby';

import SidebarMenu from './menu/sidebarMenu';
import AvatarMenu from './menu/avatarMenu';
import logoPath from '../../images/logo.png';
import { merchant, auth } from '../../state';
import LanguageSelect from '../custom/LanguageSelect/LanguageSelect';

const drawerWidth = '285px';
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    zIndex: theme.zIndex.drawer - 1,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(30),
    },
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9) + 1,
    },
  },
  drawerPaper: {
    minWidth: drawerWidth,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
}));

const sidebarHeader = ({ isLoading, isLoggingOut, merchantDetails }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const classes = useStyles();
  const theme = useTheme();

  const sm = useMediaQuery(theme.breakpoints.up('sm'));
  const trigger = useScrollTrigger();
  const { merchantName, imageUrl, id } = merchantDetails;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <div className={classes.root}>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar
          component="header"
          position="fixed"
          elevation={0}
          className={classes.appBar}
        >
          <Toolbar className={classes.appToolbar}>
            <Grid container direction="row" alignItems="center">
              {!sm ? (
                <>
                  <Grid item xs={6}>
                    <Link to="/dashboard">
                      <CardMedia
                        src={logoPath}
                        component="img"
                        alt="Pintuna logo"
                        style={{ width: '120px' }}
                      />
                    </Link>
                  </Grid>
                  <Grid item xs={6}>
                    <Box align="right">
                      <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                      >
                        <Menu fontSize="large" />
                      </IconButton>
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={3} sm={12}>
                    <Box align="right">
                      {isLoading || isLoggingOut ? (
                        <Box pr={1}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <Grid
                          container
                          alignItems="center"
                          justifyContent="flex-end"
                          spacing={3}
                        >
                          <Grid
                            item
                            style={{ marginTop: '10px', alignSelf: 'start' }}
                          >
                            <LanguageSelect />
                          </Grid>
                          <Grid item>
                            <Box>
                              <Typography variant="body1" align="right">
                                {merchantName}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" align="right">
                                ID: {id}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item>
                            <AvatarMenu
                              imageUrl={imageUrl}
                              name={merchantName}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Toolbar>
          <Divider />
        </AppBar>
      </Slide>
      <nav className={classes.drawer} aria-label="navigation">
        <Hidden smUp implementation="css">
          <Drawer
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            <SidebarMenu imageUrl={imageUrl} name={merchantName} />
          </Drawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
          >
            <SidebarMenu />
          </Drawer>
        </Hidden>
      </nav>
    </div>
  );
};

sidebarHeader.propTypes = {
  merchantDetails: PropTypes.shape({
    merchantName: PropTypes.string,
    imageUrl: PropTypes.string,
    code: PropTypes.string,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoggingOut: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  merchantDetails: merchant.selectors.selectMerchantDetails(state),
  isLoading: merchant.selectors.selectIsLoading(state),
  isLoggingOut: auth.selectors.selectIsLoading(state),
});

export default connect(mapStateToProps)(sidebarHeader);
